import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import FeatureIcon, { ICON_TONES } from '../../components/FeatureIcon';
import { premiumGold } from '../../components/canonicalAccents';
import './HostingSetup.css';

export const ProBadge: React.FC = () => {
  const { theme } = useTheme();
  const gold = premiumGold(theme);
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
      style={{ color: gold, border: `1px solid ${gold}40` }}
    >
      <FeatureIcon name="crown" size={11} />Pro
    </span>
  );
};

const useGroupColors = () => {
  const { theme, dsColors, colors } = useTheme();
  return {
    dsColors,
    colors,
    heroPrimary: colors.textPrimary,
    heroSecondary: colors.textSecondary,
    hairline: theme === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)',
  };
};

const copy = 'text-base leading-relaxed';

export const TurnoutGroup: React.FC = () => {
  const { dsColors, heroPrimary, heroSecondary, hairline } = useGroupColors();
  return (
    <div className="hs-crowd min-w-0 mt-10">
      <h3 className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-4" style={{ color: dsColors.primary }}>
        Turnout options.
      </h3>
      <ul>
        {[
          { iconName: 'shield' as const, tone: dsColors[ICON_TONES.attention], label: 'A Honda meetup', sub: 'Motorcycle brand', pro: true },
          { iconName: 'paint' as const, tone: dsColors[ICON_TONES.community], label: 'A red-bike gathering', sub: 'Motorcycle color', pro: true },
          { iconName: 'gauge' as const, tone: heroSecondary, label: 'A liter-bike event', sub: 'Minimum engine-size requirement', pro: false },
        ].map((ex) => (
          <li key={ex.label} className="flex items-center gap-3 py-4" style={{ borderTop: `1px solid ${hairline}` }}>
            <span className="inline-flex flex-shrink-0" style={{ color: ex.tone }} aria-hidden="true">
              <FeatureIcon name={ex.iconName} size={20} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-base" style={{ color: heroPrimary }}>{ex.label}</span>
              <span className="block text-xs font-bold uppercase tracking-widest mt-0.5" style={{ color: heroSecondary }}>{ex.sub}</span>
            </span>
            {ex.pro ? <ProBadge /> : (
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: heroSecondary }}>Free</span>
            )}
          </li>
        ))}
      </ul>
      <p className="text-sm mt-4" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        Engine-size requirements are available on Free. Pro adds motorcycle brand and color criteria.
      </p>
      <p className="text-sm mt-5 flex items-center gap-2" style={{ color: heroSecondary }}>
        <span className="font-semibold" style={{ color: heroPrimary }}>Event media and styling</span>
        <ProBadge />
      </p>
      <p className="text-sm mt-1" style={{ color: heroSecondary, textWrap: 'pretty' }}>
        Add flyers and videos, and choose a card theme for your event.
      </p>
    </div>
  );
};

export const CrewAndSponsors: React.FC = () => {
  const { dsColors, heroPrimary, heroSecondary } = useGroupColors();
  return (
    <div className="host-crew mt-12">
      <div className="flex items-start gap-4">
        <span className="inline-flex flex-shrink-0 mt-1" style={{ color: dsColors[ICON_TONES.community] }} aria-hidden="true">
          <FeatureIcon name="users" size={22} />
        </span>
        <div>
          <h3 className="text-xl font-bold mb-1" style={{ color: heroPrimary, textWrap: 'balance' }}>Your crew</h3>
          <p className={copy} style={{ color: heroSecondary, textWrap: 'pretty' }}>
            Invite co-hosts and crew, or review volunteers who offer to help.
          </p>
          <p className="text-sm mt-1" style={{ color: heroSecondary, textWrap: 'pretty' }}>
            Road Captains, Sweeps and Tailgunners each have a role in the plan.
          </p>
        </div>
      </div>
      <div className="host-crew-sponsors flex items-start gap-4">
        <span className="inline-flex flex-shrink-0 mt-1" style={{ color: heroSecondary }} aria-hidden="true">
          <FeatureIcon name="link" size={22} />
        </span>
        <div>
          <h3 className="text-xl font-bold mb-1" style={{ color: heroPrimary, textWrap: 'balance' }}>Your sponsors</h3>
          <p className={copy} style={{ color: heroSecondary, textWrap: 'pretty' }}>
            Give supporting businesses a place in the event with their name, logo and website.
          </p>
        </div>
      </div>
    </div>
  );
};
